import { useState } from 'react'
import { ChevronDown, CircleCheck, TriangleAlert } from 'lucide-react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

import type { WinnerImportQuote } from './winner-import.api'
import { formatEuro } from './winner-import-progress'

type Props = { quote: WinnerImportQuote }

/**
 * The money, shown so it can be trusted at a glance: the Winner total next to Soly's, the weight of
 * each supplier, and every line one click away.
 */
export function WinnerQuoteBlock({ quote }: Props) {
  const [open, setOpen] = useState(false)
  const largest = Math.max(...quote.suppliers.map((s) => s.totalExVat), 1)

  return (
    <div className="space-y-5">
      <div
        data-testid="winner-import-quote-balance"
        data-balanced={quote.isBalanced}
        className={cn(
          'flex items-start gap-3 rounded-lg border px-4 py-3',
          quote.isBalanced ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'
        )}
      >
        {quote.isBalanced ? <CircleCheck className="mt-0.5 h-5 w-5 shrink-0" /> : <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />}
        <div className="text-sm">
          <div className="font-medium">
            {quote.isBalanced ? 'Identique à Winner, au cent près' : 'Les totaux ne correspondent pas'}
          </div>
          <div className="mt-1 grid grid-cols-[auto_auto_auto] gap-x-6 gap-y-0.5 tabular-nums">
            <span />
            <span className="text-xs uppercase tracking-wide opacity-70">Winner</span>
            <span className="text-xs uppercase tracking-wide opacity-70">Soly</span>
            <span>Total HTVA</span>
            <span>{formatEuro(quote.winnerTotals.totalExVat)}</span>
            <span>{formatEuro(quote.solyTotals.totalExVat)}</span>
            <span>TVA</span>
            <span>{formatEuro(quote.winnerTotals.vatAmount)}</span>
            <span>{formatEuro(quote.solyTotals.vatAmount)}</span>
            <span className="font-medium">Total TVAC</span>
            <span className="font-medium">{formatEuro(quote.winnerTotals.totalIncVat)}</span>
            <span className="font-medium" data-amount={quote.solyTotals.totalIncVat}>{formatEuro(quote.solyTotals.totalIncVat)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Par fournisseur</div>
        {quote.suppliers.map((s) => (
          <div key={s.name} className="grid grid-cols-[10rem_1fr_10rem] items-center gap-3 text-sm">
            <span className="truncate font-medium">{s.name}</span>
            <span className="h-2 overflow-hidden rounded-full bg-slate-100">
              <span className="block h-full rounded-full bg-slate-700" style={{ width: `${(100 * s.totalExVat) / largest}%` }} />
            </span>
            <span className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
              {s.lineCount} l. · {formatEuro(s.totalExVat)}
            </span>
          </div>
        ))}
      </div>

      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          data-testid="winner-import-quote-lines-toggle"
          className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
          {open ? 'Masquer les lignes' : `Voir les ${quote.lines.length} lignes du devis`}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3 max-h-[26rem] overflow-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Réf.</th>
                  <th className="px-3 py-2 text-left font-medium">Désignation</th>
                  <th className="px-3 py-2 text-right font-medium">Qté</th>
                  <th className="px-3 py-2 text-right font-medium">PU HTVA</th>
                  <th className="px-3 py-2 text-right font-medium">Total HTVA</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {quote.lines.map((line, i) => (
                  <tr key={i} className={cn(line.role === 'model' && 'bg-slate-50/70', line.role === 'adjustment' && 'text-muted-foreground')}>
                    <td className="whitespace-nowrap px-3 py-1.5 align-top font-mono text-xs">{line.reference ?? ''}</td>
                    <td className="px-3 py-1.5 align-top">
                      <div className={cn(line.role === 'model' && 'font-medium')}>{line.title}</div>
                      {line.role === 'model' && line.description && (
                        <div className="whitespace-pre-line text-xs text-muted-foreground">{line.description}</div>
                      )}
                      {line.role === 'article' && line.brand && <div className="text-xs text-muted-foreground">{line.brand}</div>}
                    </td>
                    <td className="px-3 py-1.5 text-right align-top tabular-nums">{line.quantity ?? ''}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right align-top tabular-nums">{line.unitPrice != null ? formatEuro(line.unitPrice) : ''}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right align-top tabular-nums">{line.totalExVat != null ? formatEuro(line.totalExVat) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <p className="text-sm text-muted-foreground">
        Le devis est créé en brouillon : vous le relisez et l&apos;envoyez depuis Soly, comme les autres.
      </p>
    </div>
  )
}
